import { useCallback, useEffect, useState } from "react";
import {
  Megaphone, Plus, Pencil, Trash2, Send, Users, Check, X, ChevronDown, ChevronUp,
} from "lucide-react";
import { Select } from "antd";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import RichTextEditor from "../../components/common/RichTextEditor";
import {
  RELEASES_URL, RELEASE_DETAIL_URL, RELEASE_PUBLISH_URL,
  RELEASE_AUDIENCE_URL, RELEASE_AUDIENCE_PREVIEW_URL,
} from "../../constants/api";

const card = "bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700";
const input =
  "w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1.5 text-sm " +
  "bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500";

const EMPTY_FORM = {
  version: "", title: "", body: "", released_on: "",
  send_to_all: true, target_role_ids: [], target_user_ids: [],
};

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

function LabelInput({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
        {label}{required && <span className="text-red-500"> *</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

export default function ReleasesManager() {
  const { fetch, create, update, remove } = useApiMethods();

  const [releases, setReleases] = useState([]);
  const [roles, setRoles]       = useState([]);
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId]     = useState(null);
  const [form, setForm]         = useState(EMPTY_FORM);

  // Live count of who the current audience resolves to, so nobody publishes
  // to nobody and wonders why the popup never appeared.
  const [preview, setPreview]   = useState(null);

  // Per-release acknowledgement roster, loaded only when expanded
  const [expandedId, setExpandedId] = useState(null);
  const [recipients, setRecipients] = useState({});

  const loadReleases = useCallback(async () => {
    const res = await fetch(RELEASES_URL, {}, setLoading);
    if (res?.status === "success") setReleases(res.data || []);
  }, [fetch]);

  useEffect(() => {
    loadReleases();
    (async () => {
      // Both lists come from the releases app rather than /auth/*, which is
      // super_admin only — an admin granted 'releases' needs them too.
      const res = await fetch(RELEASE_AUDIENCE_URL);
      if (res?.status === "success") {
        setRoles(res.roles || []);
        setUsers(res.users || []);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Audience preview — refreshed whenever the targeting changes.
  useEffect(() => {
    if (!showForm) return;
    let cancelled = false;
    (async () => {
      const res = await create(RELEASE_AUDIENCE_PREVIEW_URL, {
        send_to_all: form.send_to_all,
        target_role_ids: form.target_role_ids,
        target_user_ids: form.target_user_ids,
      });
      if (!cancelled && res?.status === "success") setPreview(res);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForm, form.send_to_all, form.target_role_ids, form.target_user_ids]);

  const openNew = () => {
    setForm(EMPTY_FORM);
    setEditId(null);
    setPreview(null);
    setShowForm(true);
  };

  const openEdit = (release) => {
    setForm({
      version:         release.version || "",
      title:           release.title || "",
      body:            release.body || "",
      released_on:     release.released_on || "",
      send_to_all:     !!release.send_to_all,
      target_role_ids: release.target_role_ids || [],
      target_user_ids: release.target_user_ids || [],
    });
    setEditId(release.id);
    setPreview(null);
    setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setEditId(null); setForm(EMPTY_FORM); };

  const save = async (publish) => {
    if (!form.title.trim()) return ToastMsg("Title is required", "error");
    if (!form.send_to_all && !form.target_role_ids.length && !form.target_user_ids.length)
      return ToastMsg("Pick at least one role or person, or send to everyone", "error");

    const payload = { ...form, title: form.title.trim(), released_on: form.released_on || null };
    const res = editId
      ? await update(RELEASE_DETAIL_URL(editId), payload, false, setSaving)
      : await create(RELEASES_URL, { ...payload, publish }, false, setSaving);

    if (res?.status !== "success") {
      return ToastMsg(res?.message || "Failed to save release", "error");
    }
    // An edited draft that the admin chose to send needs the explicit publish
    // call — the PUT above only ever updates.
    if (editId && publish) {
      const pub = await create(RELEASE_PUBLISH_URL(editId), {}, false, setSaving);
      if (pub?.status !== "success") {
        loadReleases();
        return ToastMsg(pub?.message || "Saved, but could not send", "error");
      }
      ToastMsg(pub.message || "Release sent", "success");
    } else {
      ToastMsg(res.message || "Saved", "success");
    }
    closeForm();
    loadReleases();
  };

  const publishExisting = async (release) => {
    const verb = release.status === "published" ? "Re-send" : "Send";
    if (!window.confirm(
      `${verb} "${release.title}"? Everyone in its audience who has not already ` +
      `acknowledged it will see the popup at their next sign-in.`
    )) return;
    const res = await create(RELEASE_PUBLISH_URL(release.id), {}, false, setSaving);
    if (res?.status === "success") {
      ToastMsg(res.message || "Release sent", "success");
      loadReleases();
    } else {
      ToastMsg(res?.message || "Failed to send", "error");
    }
  };

  const deleteRelease = async (release) => {
    if (!window.confirm(`Delete "${release.title}"? It disappears for everyone, acknowledged or not.`)) return;
    const res = await remove(RELEASE_DETAIL_URL(release.id), {});
    if (res?.status === "success") { ToastMsg("Release deleted", "success"); loadReleases(); }
    else ToastMsg(res?.message || "Failed to delete", "error");
  };

  const toggleRoster = async (release) => {
    if (expandedId === release.id) return setExpandedId(null);
    setExpandedId(release.id);
    if (recipients[release.id]) return;
    const res = await fetch(RELEASE_DETAIL_URL(release.id));
    if (res?.status === "success") {
      setRecipients((prev) => ({ ...prev, [release.id]: res.recipients || [] }));
    }
  };

  const roleOptions = roles.map((r) => ({ value: r.id, label: r.name }));
  const userOptions = users.map((u) => ({
    value: u.id,
    label: u.role_name ? `${u.name} — ${u.role_name}` : u.name,
  }));

  return (
    <ContainerCard>
      <div className="p-4 max-w-4xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Megaphone className="h-5 w-5 text-primary" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Release Notes</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Announce an update and see who has acknowledged it.
              </p>
            </div>
          </div>
          {!showForm && (
            <Button size="sm" onClick={openNew}>
              <Plus className="h-4 w-4 mr-1" /> New Release
            </Button>
          )}
        </div>

        {/* ── Compose ─────────────────────────────────────────────── */}
        {showForm && (
          <div className={`${card} p-5 mb-5 space-y-4`}>
            <p className="text-sm font-semibold text-gray-900 dark:text-white">
              {editId ? "Edit Release" : "New Release"}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <LabelInput label="Title" required>
                <input className={input} placeholder="What changed"
                  value={form.title}
                  onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} />
              </LabelInput>
              <LabelInput label="Version">
                <input className={input} placeholder="e.g. v2.4.0"
                  value={form.version}
                  onChange={(e) => setForm((p) => ({ ...p, version: e.target.value }))} />
              </LabelInput>
              <LabelInput label="Release Date">
                <input type="date" className={input}
                  value={form.released_on}
                  onChange={(e) => setForm((p) => ({ ...p, released_on: e.target.value }))} />
              </LabelInput>
            </div>

            <LabelInput label="Details">
              <RichTextEditor
                value={form.body}
                onChange={(html) => setForm((p) => ({ ...p, body: html }))}
                placeholder="What's new, and what to do differently…"
              />
              {/* Cleared past the editor's own height so the hint does not sit
                  underneath the toolbar. */}
              <p className="mt-12 text-[11px] text-gray-400">
                Formatting is kept. Links open in a new tab.
              </p>
            </LabelInput>

            {/* Audience */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-3">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                Who gets this
              </p>
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                <input type="checkbox" className="rounded"
                  checked={form.send_to_all}
                  onChange={(e) => setForm((p) => ({ ...p, send_to_all: e.target.checked }))} />
                Everyone
              </label>

              {!form.send_to_all && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <LabelInput label="Roles">
                    <Select mode="multiple" allowClear className="w-full" placeholder="Any role"
                      value={form.target_role_ids} options={roleOptions}
                      optionFilterProp="label"
                      onChange={(v) => setForm((p) => ({ ...p, target_role_ids: v }))} />
                  </LabelInput>
                  <LabelInput label="Individual People">
                    <Select mode="multiple" allowClear className="w-full" placeholder="Named people"
                      value={form.target_user_ids} options={userOptions}
                      optionFilterProp="label"
                      onChange={(v) => setForm((p) => ({ ...p, target_user_ids: v }))} />
                  </LabelInput>
                </div>
              )}

              {preview && (
                <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                  <Users className="h-3.5 w-3.5" />
                  {preview.count === 0
                    ? "No one matches this audience yet."
                    : `${preview.count} ${preview.count === 1 ? "person" : "people"}` +
                      (preview.names?.length ? ` — ${preview.names.slice(0, 5).join(", ")}` : "") +
                      (preview.count > 5 ? ", …" : "")}
                </p>
              )}
            </div>

            <div className="flex gap-2 pt-1">
              <Button size="sm" onClick={() => save(true)} disabled={saving}>
                <Send className="h-3.5 w-3.5 mr-1" /> {editId ? "Save & Send" : "Publish"}
              </Button>
              <Button size="sm" variant="outline" onClick={() => save(false)} disabled={saving}>
                Save as Draft
              </Button>
              <Button size="sm" variant="ghost" onClick={closeForm} disabled={saving}>Cancel</Button>
            </div>
          </div>
        )}

        {/* ── List ────────────────────────────────────────────────── */}
        {loading ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : releases.length === 0 ? (
          <div className={`${card} p-8 flex flex-col items-center gap-3 text-gray-400 dark:text-gray-500`}>
            <Megaphone className="h-8 w-8 opacity-30" />
            <p className="text-sm">No releases yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {releases.map((release) => {
              const ack = release.ack || {};
              const isDraft = release.status === "draft";
              return (
                <div key={release.id} className={`${card} p-4 space-y-3`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">
                          {release.title}
                        </span>
                        {release.version && (
                          <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-primary/10 text-primary">
                            {release.version}
                          </span>
                        )}
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${
                          isDraft
                            ? "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                            : "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                        }`}>
                          {isDraft ? "Draft" : "Published"}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {fmtDate(release.released_on)}
                        {release.send_to_all ? " · Everyone" : " · Selected roles / people"}
                      </p>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button size="sm" variant="outline" onClick={() => publishExisting(release)}>
                        <Send className="h-3.5 w-3.5 mr-1" /> {isDraft ? "Send" : "Re-send"}
                      </Button>
                      <button onClick={() => openEdit(release)}
                        className="p-1.5 rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700" title="Edit">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => deleteRelease(release)}
                        className="p-1.5 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20" title="Delete">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {release.excerpt && (
                    <p className="text-xs text-gray-600 dark:text-gray-300 line-clamp-2">
                      {release.excerpt}
                    </p>
                  )}

                  {/* Acknowledgement progress */}
                  {!isDraft && (
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
                      <button type="button" onClick={() => toggleRoster(release)}
                        className="w-full flex items-center justify-between gap-3 text-left">
                        <div className="flex-1">
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="font-medium text-gray-600 dark:text-gray-300">
                              {ack.acknowledged} of {ack.total} acknowledged
                            </span>
                            <span className="text-gray-400">{ack.percent ?? 0}%</span>
                          </div>
                          <div className="h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                            <div className="h-full bg-green-500 rounded-full transition-all"
                              style={{ width: `${ack.percent ?? 0}%` }} />
                          </div>
                        </div>
                        {expandedId === release.id
                          ? <ChevronUp className="h-4 w-4 text-gray-400 shrink-0" />
                          : <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />}
                      </button>

                      {expandedId === release.id && (
                        <ul className="mt-3 divide-y divide-gray-100 dark:divide-gray-700 max-h-56 overflow-y-auto">
                          {(recipients[release.id] || []).map((r) => (
                            <li key={r.user_id} className="flex items-center justify-between py-1.5 text-xs">
                              <span className="text-gray-700 dark:text-gray-200">{r.user_name}</span>
                              {r.acknowledged_at ? (
                                <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                                  <Check className="h-3.5 w-3.5" /> {fmtDate(r.acknowledged_at)}
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 text-gray-400">
                                  <X className="h-3.5 w-3.5" />
                                  {r.shown_count ? `shown ${r.shown_count}×` : "not shown yet"}
                                </span>
                              )}
                            </li>
                          ))}
                          {!(recipients[release.id] || []).length && (
                            <li className="py-2 text-xs text-gray-400">No recipients</li>
                          )}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </ContainerCard>
  );
}
